import { useCallback, useMemo, useState } from 'react';
import { Box, Avatar } from '@mui/material';
import { useSnackbar } from 'notistack';
import { QDropdownWrapper, QTypography, QDataTable, QButton } from '@kw/quokka-ui';

import { useFetchService } from '../../utils/fetchService';
import { sendHttpRequest } from '../../utils/network.service';
import { useLocalStorage } from '../../utils/useLocalStorage';

export const Roles = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedBulkRole, setSelectedBulkRole] = useState<string>('');
  const [accessToken = ''] = useLocalStorage('accessToken', '');

  const { data: rolesResp } = useFetchService('/organizations/global/roles', accessToken);

  // Only fetch users when a role is selected (avoid /roles//users)
  const usersPath = selectedRole ? `/organizations/global/roles/${selectedRole}/users` : '';
  const { data: usersResp } = useFetchService(usersPath, accessToken);

  const users = useMemo(() => usersResp?.users ?? [], [usersResp]);

  const roleOptions = useMemo(
    () =>
      (rolesResp?.roles ?? []).map(r => ({
        value: r.id,
        label: r.name,
      })),
    [rolesResp?.roles],
  );

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        renderCell: row => (
          <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={row.picture} />
            <span>{row.name || '(no name)'}</span>
          </Box>
        ),
      },
      { field: 'user_id', headerName: 'User ID' },
      { field: 'email', headerName: 'Email' },
    ],
    [],
  );

  const canBulkAct = Boolean(selectedBulkRole && users.length > 0);

  const grantAllUsersRole = useCallback(async () => {
    const userIds = users.map(u => u.user_id);
    if (!selectedBulkRole || userIds.length === 0) {
      enqueueSnackbar('Select a role and ensure there are users to update.', { variant: 'warning' });
      return;
    }

    await sendHttpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/global/roles/${selectedBulkRole}/add-users`,
      method: 'POST',
      body: { users: userIds },
      successMessage: 'All users have been granted the role successfully.',
      errorMessage: 'Failed to grant role to users.',
      enqueueSnackbar,
    });
  }, [enqueueSnackbar, selectedBulkRole, users]);

  const removeAllUsersRole = useCallback(async () => {
    const userIds = users.map(u => u.user_id);
    if (!selectedBulkRole || userIds.length === 0) {
      enqueueSnackbar('Select a role and ensure there are users to update.', { variant: 'warning' });
      return;
    }

    await sendHttpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/global/roles/${selectedBulkRole}/remove-users`,
      method: 'DELETE',
      body: { users: userIds },
      successMessage: 'All users have been removed from the role successfully.',
      errorMessage: 'Failed to remove role from users.',
      enqueueSnackbar,
    });
  }, [enqueueSnackbar, selectedBulkRole, users]);

  return (
    <>
      <Box p={2}>
        <QTypography variant="h4Bold">Roles</QTypography>

        <QDropdownWrapper
          selectedOption={selectedRole}
          closedLabel={roleOptions.find(o => o.value === selectedRole)?.label ?? 'Select Role To View Users'}
          formControlProps={{ sx: { width: '100%' } }}
          onChange={e => setSelectedRole(e.target.value as string)}
          options={roleOptions}
        />
      </Box>

      <QDataTable emptyTableText="No Users Found" columns={columns} data={users} searchConfig={['user_id', 'name', 'email']} />

      <Box mt={2}>
        <QDropdownWrapper
          selectedOption={selectedBulkRole}
          closedLabel={roleOptions.find(o => o.value === selectedBulkRole)?.label ?? 'Select Role For Bulk Action'}
          formControlProps={{ sx: { width: '100%' } }}
          onChange={e => setSelectedBulkRole(e.target.value as string)}
          options={roleOptions}
        />

        <Box mt={2}>
          <QButton variant="filled" disabled={!canBulkAct} onClick={grantAllUsersRole}>
            Grant all users role
          </QButton>
          <QButton sx={{ ml: 1 }} variant="outlined" disabled={!canBulkAct} onClick={removeAllUsersRole}>
            Remove all users role
          </QButton>
        </Box>
      </Box>
    </>
  );
};
